import React, {createContext, useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import axios from 'axios';
import {config} from '../init/config';
import {AUTH_QUERY_KEY} from '../init/constants';
import {noop} from 'lodash';
import {getRoleNames} from '../utils/UserUtils';

interface AuthContext {
    readonly isAuthenticated: boolean;
    readonly isAuthenticating: boolean;
    readonly token: string | null;
    readonly roles: string[];
    readonly jti: string | null;
    refreshToken: () => Promise<void>;
}

interface JwtPayloadWithRoles extends JwtPayload {
    readonly realm_access: { roles: string[] };
    readonly resource_access: { dls_dxf: { roles: string[] } };
}

interface AuthResponse {
    readonly expiredAt?: string;
    readonly token?: string;
    readonly message?: string;
}

export const AuthContext = createContext<AuthContext>({
    isAuthenticated: false,
    isAuthenticating: false,
    token: null,
    roles: [],
    jti: null,
    refreshToken: () => Promise.reject()
});

const AuthContextProvider: React.FC = (props) => {
    const [token, setToken] = useState<string | null>(null);
    const [jti, setJti] = useState<string | null>(null);
    const [roles, setRoles] = useState<string[]>([]);

    const {data, refetch, isLoading} = useQuery(AUTH_QUERY_KEY, async () => {
        if ((config.DLS_AUTHENTICATION_ACTIVE) && (process.env.NODE_ENV === 'production')) {
            const response = await axios.get<AuthResponse>(config.DLS_API_TOKEN_PATH);
            return response.data;
        } else {
            const token = localStorage.getItem('auth-token');
            return {
                token: token,
                expiredAt: '9999'
            };
        }
    });

    useEffect(() => {
        const token = data?.token ?? null;
        setToken(isTokenValid(token) ? token : null);
        const roles = getRolesFromToken(token);
        setJti(token ? jwtDecode<JwtPayload>(token)?.jti || null : null);
        setRoles(roles);
    }, [data, config.DLS_AUTHENTICATION_ACTIVE, config.DLS_API_TOKEN_PATH, config.DLS_USE_CLIENT_ROLES]);

    const getRolesFromToken = (token: string | null) => {
        if (!token) {
            return [];
        }

        try {
            const decodedToken = jwtDecode<JwtPayloadWithRoles>(token);
            if (config.DLS_USE_CLIENT_ROLES) {
                return getRoleNames(decodedToken.resource_access.dls_dxf.roles);
            } else {
                return getRoleNames(decodedToken.realm_access.roles);
            }
        } catch {
            return [];
        }
    };

    const isTokenValid = (token: string | null) => {
        if (!token) {
            return false;
        }

        try {
            const decodedToken = jwtDecode<JwtPayload>(token);
            if (decodedToken.exp) {
                let timeDiff = (decodedToken.exp * 1000) - Date.now();
                if (timeDiff > 0) {
                    setTimeout(refreshToken, timeDiff + 1)
                }
                return (timeDiff > 0)
            }
        } catch (err) {
            return err;
        }
    };

    const refreshToken = (): Promise<void> => {
        return refetch().then(noop);
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated: !!token,
                isAuthenticating: isLoading,
                token: token,
                roles: roles,
                jti,
                refreshToken: refreshToken
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;